import { faEnvelope } from '@fortawesome/free-solid-svg-icons';
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconChevronDown, IconChevronRight } from '@tabler/icons-react';
import { ColumnDef, Row } from '@tanstack/react-table';
import { FIELD_CONFIG } from 'configs/columns';
import {
  formatCentsToDollars,
  toPercentage,
} from 'features/pages/marketplace/BiddingAndPricing/formatting';
import { Link } from 'react-router-dom';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { theme } from 'style/ORSNNTheme';
import { match } from 'utils/functionalMatch';

import { DealBlocker, DealStatus } from '__generated__/globalTypes';

import {
  GetSellerListings_user_company_offerings_UserCompanyListing as Listing,
  GetSellerListings_user_company_offerings_UserCompanyListing_deals as Deals,
  GetSellerListings_user_company_offerings_UserCompanyListing_deals_state,
} from './__generated__/GetSellerListings';

type DealWithRole = Deals & { role: DealBlocker };

function isListing(original: Listing | Deals): original is Listing {
  return 'deals' in original;
}

type RowIconsProps = {
  row: Row<Listing | DealWithRole>;
};

const RowIcons = (props: RowIconsProps) => {
  const isExpanded = props.row.getIsExpanded();
  const Icon = isExpanded ? IconChevronDown : IconChevronRight;
  const original = props.row.original;
  const hasNewMessages = (() => {
    if (isListing(original)) {
      return original.deals.some((deal) => deal.hasNewMessages);
    } else {
      return original.hasNewMessages;
    }
  })();

  return (
    <span className="flex flex-row items-center justify-start [&>*]:mr-[5px]">
      {hasNewMessages ? (
        <span className="block bg-warning-subtle px-[5px]">
          <FontAwesomeIcon
            icon={faEnvelope}
            color={hasNewMessages ? theme.color.warningDefault : theme.color.warningDefault}
          />
        </span>
      ) : (
        <></>
      )}
      {props.row.getCanExpand() && <Icon />}
    </span>
  );
};

const StatusColors = {
  UserAction: theme.color.warningDefault,
  CounterpartyAction: theme.color.gray900,
  Complete: theme.color.successDefault,
  Rejected: theme.color.gray900,
};

function getContrastYIQ(hexColor: string) {
  const r = parseInt(hexColor.substring(1, 2), 16);
  const g = parseInt(hexColor.substring(3, 2), 16);
  const b = parseInt(hexColor.substring(5, 2), 16);
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 128 ? 'black' : 'white';
}

const LoanPoolLink = (props: { row: Row<Listing | DealWithRole> }) => {
  const original = props.row.original;

  if (isListing(original)) {
    const hasOneDeal = original.deals.length === 1;
    const link = hasOneDeal
      ? `../deal/${encodeURIComponent(original.deals[0].id)}`
      : `../listing/${encodeURIComponent(original.id)}`;
    return (
      <Link
        className="text-white no-underline hover:cursor-pointer hover:text-gray-600 hover:underline"
        to={link}
      >
        {original.providedName}
      </Link>
    );
  } else if ('listing' in original) {
    return (
      <Link
        className="text-white no-underline hover:cursor-pointer hover:text-gray-600 hover:underline"
        to={`../deal/${encodeURIComponent(original.id)}`}
      >
        View
      </Link>
    );
  } else {
    return <></>;
  }
};

export function createPoolStatusCell<T extends DealBlocker>(_dealBlockerType: T) {
  return (props: { row: Row<Listing | DealWithRole> }) => {
    const original = props.row.original;
    const hasOneDeal = isListing(original) && original.deals.length === 1;
    const deal = (hasOneDeal ? original.deals[0] : original) as DealWithRole;
    if ((isListing(original) && hasOneDeal) || ('listing' in original && original.listing)) {
      const statusColor = match<
        GetSellerListings_user_company_offerings_UserCompanyListing_deals_state,
        string
      >(deal.state)
        .on(
          (state) => state?.status === DealStatus.COMPLETE,
          () => StatusColors.Complete,
        )
        .on(
          (state) => state?.status === DealStatus.REJECTED_INQUIRY,
          () => StatusColors.Rejected,
        )
        .on(
          (state) =>
            state?.blocker === deal.role || // TODO adjust this to work for buyers
            state?.blocker === DealBlocker.BOTH,
          () => StatusColors.UserAction,
        )
        .otherwise(() => StatusColors.CounterpartyAction);

      return (
        <>
          <span
            className={`rounded px-2 py-px text-${getContrastYIQ(statusColor)}`}
            data-tooltip-id={deal.id}
            style={{ backgroundColor: statusColor }}
          >
            {deal.state.label}
          </span>
          <ReactTooltip className="z-20 max-w-64 whitespace-normal break-words" id={deal.id}>
            {deal.state.tooltip}
          </ReactTooltip>
        </>
      );
    } else {
      return <></>;
    }
  };
}

export const activeTradesAllTradesColumnOrder: string[] = [
  'selection',
  'icons',
  'providedName',
  'counterparty.name',
  'offered',
  'state.label',
  'createdDate',
  'assetClass',
  'upb',
  'loan_count',
  'wa_coupon_bps',
  'wa_loan_age_months',
  'wa_remaining_loan_terms_months',
  'wa_borrower_credit_score',
  'wa_ltv_bps',
];

export const activeListingsColumnDefinitions: ColumnDef<Listing | DealWithRole>[] = [
  {
    id: 'icons',
    cell: RowIcons,
    meta: {
      headerNoPill: true,
    },
  },
  {
    accessorFn: (row: Listing | Deals) => {
      if (isListing(row)) {
        return row.providedName;
      } else {
        return row.listing.providedName;
      }
    },
    header: 'Loan Pool Name',
    id: 'providedName',
    cell: LoanPoolLink,
  },
  {
    accessorKey: 'offered',
    id: 'offered',
    header: 'Offered',
    cell: (_props) => {
      return (
        <span>
          <FontAwesomeIcon icon={faCheckCircle} color="#5DC389" size="2xl" />
        </span>
      );
    },
  },
  {
    accessorKey: 'state.label',
    id: 'state.label',
    header: 'Offer Status',
    cell: createPoolStatusCell(DealBlocker.SELLER),
  },
  {
    accessorKey: 'createdDate',
    id: 'createdDate',
    header: 'Date Created',
    cell: (props) => {
      return <span>{FIELD_CONFIG.maturity_date.display(props.getValue() as string)}</span>;
    },
  },
  {
    accessorKey: 'counterparty.name',
    id: 'counterparty.name',
    header: 'Counterparty',
  },
  {
    header: 'Asset Class',
    id: 'assetClass',
    accessorFn: (row: Listing | Deals) => {
      if ('assetClass' in row) {
        return row.assetClass === 'HOME' ? 'RESI' : row.assetClass;
      }
      if ('listing' in row && 'assetClass' in row.listing) {
        return row.listing.assetClass === 'HOME' ? 'RESI' : row.listing.assetClass;
      }
      // Handle other cases or return a default value
      return undefined;
    },
  },
  {
    accessorKey: 'upb',
    id: 'upb',
    header: 'UPB',
    meta: {
      numeric: true,
    },
    cell: (props) => <span>{formatCentsToDollars(Number(props.getValue()))}</span>,
  },
  {
    accessorKey: 'loan_count',
    id: 'loan_count',
    header: '# Loans',
    meta: {
      numeric: true,
    },
  },
  {
    accessorKey: 'wa_coupon_bps',
    id: 'wa_coupon_bps',
    header: 'GWAC',
    cell: (props) => <span>{toPercentage(Number(props.getValue()))}</span>,
  },
  {
    accessorKey: 'wa_loan_age_months',
    id: 'wa_loan_age_months',
    header: 'WALA',
    meta: {
      numeric: true,
    },
  },
  {
    accessorKey: 'wa_remaining_loan_terms_months',
    id: 'wa_remaining_loan_terms_months',
    header: 'WAM',
    meta: {
      numeric: true,
    },
  },
  {
    accessorKey: 'wa_borrower_credit_score',
    id: 'wa_borrower_credit_score',
    header: 'FICO',
    meta: {
      numeric: true,
    },
  },
  {
    accessorKey: 'wa_ltv_bps',
    id: 'wa_ltv_bps',
    header: 'LTV',
    cell: (props) => <span>{toPercentage(Number(props.getValue()))}</span>,
    meta: {
      numeric: true,
    },
  },
];
